import { onScopeDispose, ref, shallowRef } from 'vue'

export type HoverIntentSource = 'pointer' | 'click'

/** How long the card survives after the pointer leaves, so it can be crossed into. */
const DEFAULT_CLOSE_DELAY_MS = 300

/**
 * Open/close timing for a hover card: a grace period on close, and a record of what opened it.
 *
 * `openedBy` is load-bearing. A click must only ever close a card that a PREVIOUS click opened: on
 * touch, focus (and, before the `(hover: hover)` guard, mouseenter) fires first, so a naive toggle
 * closed the card the very same tap had just opened — making the tap look dead.
 */
export function useHoverIntent<T>(closeDelayMs: number = DEFAULT_CLOSE_DELAY_MS) {
  const target = shallowRef<T | null>(null)
  const openedBy = ref<HoverIntentSource | null>(null)
  let closeTimer: ReturnType<typeof setTimeout> | null = null

  function cancelClose() {
    if (closeTimer) { clearTimeout(closeTimer); closeTimer = null }
  }

  function open(next: T, source: HoverIntentSource = 'pointer') {
    cancelClose()
    openedBy.value = source
    target.value = next
  }

  function close() {
    cancelClose()
    target.value = null
    openedBy.value = null
  }

  function scheduleClose() {
    cancelClose()
    closeTimer = setTimeout(close, closeDelayMs)
  }

  onScopeDispose(cancelClose)

  return { target, openedBy, open, close, scheduleClose, cancelClose }
}
